"use client";

import Link from "next/link";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h2 className="mb-4 mt-10 font-serif text-[clamp(22px,3vw,30px)] font-black text-(--mk-cream)">
      {children}
    </h2>
  ),
  h2: ({ children }) => (
    <h2 className="mb-4 mt-10 font-serif text-[clamp(22px,3vw,30px)] font-black text-(--mk-cream)">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-3 mt-8 font-serif text-xl font-bold text-(--mk-cream)">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="mb-5 text-base leading-[1.8] text-(--mk-cream-muted)">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="mb-5 list-disc space-y-2 pl-6 text-base leading-[1.8] text-(--mk-cream-muted)">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-5 list-decimal space-y-2 pl-6 text-base leading-[1.8] text-(--mk-cream-muted)">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  a: ({ href, children }) => {
    const isExternal = href?.startsWith("http");
    return (
      <Link
        href={href ?? "#"}
        className="cursor-pointer text-(--mk-gold-light) underline decoration-(--mk-gold-border) underline-offset-4 hover:text-(--mk-gold)"
        {...(isExternal
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {children}
      </Link>
    );
  },
  strong: ({ children }) => (
    <strong className="font-semibold text-(--mk-cream)">{children}</strong>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-5 border-l-2 border-(--mk-gold-border-strong) pl-4 text-(--mk-cream-60)">
      {children}
    </blockquote>
  ),
  img: ({ src, alt }) =>
    typeof src === "string" ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt={alt ?? ""}
        className="my-6 w-full rounded-2xl border border-(--mk-cream-whisper)"
      />
    ) : null,
  code: ({ children }) => (
    <code className="rounded-md bg-(--mk-cream-surface) px-1.5 py-0.5 text-sm text-(--mk-gold-light)">
      {children}
    </code>
  ),
  hr: () => <hr className="my-10 border-(--mk-cream-whisper)" />,
};

export function BlogMarkdown({ content }: { content: string }) {
  return (
    <div className="blog-markdown">
      <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
    </div>
  );
}
